/**
 * AsignarPresupuestoCostoModal
 * Modal para seleccionar áreas del PresupuestoVenta y crear PresupuestoCosto con su estructura
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiCheck } from 'react-icons/fi';
import presupuestoHierarchyService from '../services/presupuestoHierarchyService';
import { presupuestoCostoService } from '../services/onebaseService';
import { toast } from 'react-toastify';
import '../styles/modals.css';
import './AsignarPresupuestoCostoModal.css';

import Loading from './Loading';
const AsignarPresupuestoCostoModal = ({
  isOpen,
  presupuestoVenta,
  onClose,
  onSuccess
}) => {
  const [areasConEstructura, setAreasConEstructura] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creando, setCreando] = useState(false);

  useEffect(() => {
    if (isOpen && presupuestoVenta) {
      loadAreasEstructura();
    }
  }, [isOpen, presupuestoVenta]);

  const loadAreasEstructura = async () => {
    setLoading(true);
    try {
      const response = await presupuestoHierarchyService.getVentaAreasForPresupuesto(presupuestoVenta.id);
      const areas = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setAreasConEstructura(areas);
      // Seleccionar todas por defecto
      setSelectedAreas(areas.map(a => a.id));
    } catch (error) {
      console.error('Error loading áreas:', error);
      toast.error('Error al cargar las áreas del presupuesto');
    } finally {
      setLoading(false);
    }
  };

  const toggleAreaSelection = (areaId) => {
    if (selectedAreas.includes(areaId)) {
      setSelectedAreas(selectedAreas.filter(id => id !== areaId));
    } else {
      setSelectedAreas([...selectedAreas, areaId]);
    }
  };

  const handleAsignarPresupuesto = async () => {
    if (selectedAreas.length === 0) {
      toast.warning('Selecciona al menos un área');
      return;
    }

    setCreando(true);
    try {
      // 1. Crear presupuesto de costo
      const nuevoPresupuestoCosto = {
        proyecto: presupuestoVenta.proyecto,
        estado: 'borrador',
        descripcion: `Presupuesto de costo para: ${presupuestoVenta.descripcion || presupuestoVenta.nombre || 'Presupuesto'}`,
      };

      const presupuestoCostoResponse = await presupuestoCostoService.create(nuevoPresupuestoCosto);
      const presupuestoCostoCreado = presupuestoCostoResponse.data;

      // 2. Copiar áreas, partidas y recursos seleccionadas
      const areasSeleccionadas = areasConEstructura.filter(a => selectedAreas.includes(a.id));

      for (const area of areasSeleccionadas) {
        // Crear PresupuestoCostoArea
        const nuevaAreaCosto = {
          presupuesto_costo: presupuestoCostoCreado.id,
          area_referencia: area.area_referencia,
          nombre: area.nombre,
          descripcion: area.descripcion,
        };

        const areaResponse = await presupuestoHierarchyService.createCostoArea(nuevaAreaCosto);
        const areaCostoCreada = areaResponse.data;

        // Copiar partidas de esta área
        if (area.partidas_estructura && area.partidas_estructura.length > 0) {
          for (const partida of area.partidas_estructura) {
            // Crear PresupuestoCostoPartida
            const nuevaPartidaCosto = {
              area: areaCostoCreada.id,
              partida_referencia: partida.partida_referencia,
              nombre: partida.nombre,
              descripcion: partida.descripcion,
              unidad: partida.unidad,
              cantidad: partida.cantidad,
            };

            const partidaResponse = await presupuestoHierarchyService.createCostoPartida(nuevaPartidaCosto);
            const partidaCostoCreada = partidaResponse.data;

            // Copiar recursos de esta partida
            if (partida.recursos_estructura && partida.recursos_estructura.length > 0) {
              for (const recurso of partida.recursos_estructura) {
                const nuevoRecursoCosto = {
                  partida: partidaCostoCreada.id,
                  recurso_referencia: recurso.recurso_referencia,
                  nombre: recurso.nombre,
                  descripcion: recurso.descripcion,
                  cantidad_presupuesto: recurso.cantidad_presupuesto,
                  precio_unitario_costo: recurso.precio_unitario_costo,
                  precio_unitario_venta: recurso.precio_unitario_venta,
                  markup: recurso.markup,
                };

                await presupuestoHierarchyService.createCostoRecurso(nuevoRecursoCosto);
              }
            }
          }
        }
      }

      toast.success(`Presupuesto de costo creado con ${areasSeleccionadas.length} área(s)`);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error creating presupuesto costo:', error);
      toast.error(error.response?.data?.detail || 'Error al crear el presupuesto de costo');
    } finally {
      setCreando(false);
    }
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg asignar-presupuesto-costo-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Asignar Presupuesto a Costos</h2>
          <button className="close-btn" onClick={onClose} disabled={creando}>
            <FiX />
          </button>
        </div>

        {/* Content */}
        <div className="modal-body">
          <div className="info-message">
            <p>Selecciona las áreas que deseas asignar al presupuesto de costo. Se copiarán todas las partidas y recursos asociados.</p>
          </div>

          {loading ? (
            <Loading fullScreen={false} size="sm" mensaje="Cargando áreas" />
          ) : areasConEstructura.length === 0 ? (
            <div className="empty-state">No hay áreas en este presupuesto</div>
          ) : (
            <div className="areas-container">
              {areasConEstructura.map(area => (
                <div key={area.id} className="area-selection-item">
                  <div className="area-checkbox">
                    <input
                      type="checkbox"
                      id={`area-${area.id}`}
                      checked={selectedAreas.includes(area.id)}
                      onChange={() => toggleAreaSelection(area.id)}
                      disabled={creando}
                    />
                  </div>
                  <label htmlFor={`area-${area.id}`} className="area-info">
                    <div className="area-name">{area.nombre}</div>
                    {area.descripcion && <div className="area-description">{area.descripcion}</div>}
                    <div className="area-stats">
                      <span className="stat">{area.partidas_estructura?.length || 0} partida(s)</span>
                      <span className="stat">
                        {(area.partidas_estructura || []).reduce((sum, p) => sum + (p.recursos_estructura?.length || 0), 0)} recurso(s)
                      </span>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          )}

          {areasConEstructura.length > 0 && (
            <div className="selection-summary">
              {selectedAreas.length} de {areasConEstructura.length} área(s) seleccionada(s)
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={creando}
          >
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAsignarPresupuesto}
            disabled={creando || selectedAreas.length === 0}
          >
            {creando ? 'Creando...' : (
              <>
                <FiCheck /> Asignar a Costos
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.getElementById('modal-root'));
};

export default AsignarPresupuestoCostoModal;
